export const AUTH_STORAGE_KEY = "auth";
export const SELECTED_PHARMACY_STORAGE_KEY = "selectedPharmacy";
export const SESSION_EXPIRED_STORAGE_KEY = "sessionExpired";
export const LAST_ACTIVITY_STORAGE_KEY = "lastActivity";
export const AUTH_SESSION_CLEARED_EVENT = "auth-session-cleared";

// Données métier gardées par le navigateur (SEC-008) : retirées du poste à la déconnexion,
// à l'expiration et au changement d'utilisateur. Les préférences d'affichage restent.
export const BUSINESS_STORAGE_KEYS = ["cno-patients", "robot-planning-productions", SELECTED_PHARMACY_STORAGE_KEY];
export const BUSINESS_STORAGE_PREFIXES = ["commande-draft:"];

const sessionCleanups = new Set();

/** Enregistre un nettoyage à lancer à chaque purge de session ; renvoie la fonction de désinscription. */
export function registerSessionCleanup(cleanup) {
    sessionCleanups.add(cleanup);
    return () => sessionCleanups.delete(cleanup);
}

function purgeBusinessData() {
    sessionCleanups.forEach(cleanup => {
        try {
            cleanup();
        } catch {
            // Un nettoyage en erreur ne doit pas empêcher les autres.
        }
    });
    try {
        Object.keys(localStorage)
            .filter(key => BUSINESS_STORAGE_KEYS.includes(key)
                || BUSINESS_STORAGE_PREFIXES.some(prefix => key.startsWith(prefix)))
            .forEach(key => localStorage.removeItem(key));
    } catch {
        // localStorage can be unavailable in private browsing or tests.
    }
}

const EXPIRATION_LEEWAY_SECONDS = 30;
export const REFRESH_LEEWAY_SECONDS = 5 * 60;
export const DEFAULT_IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;
export const LIVREUR_IDLE_TIMEOUT_MS = 20 * 60 * 60 * 1000;

export function getIdleTimeoutMs(role) {
    return role === "LIVREUR" ? LIVREUR_IDLE_TIMEOUT_MS : DEFAULT_IDLE_TIMEOUT_MS;
}

// Le JWT voyage uniquement dans le cookie HttpOnly posé par le serveur (SEC-008). Le navigateur ne garde
// qu'un profil de session (rôle, nom, email, pharmacie, expiration) pour l'affichage et l'inactivité.
const PROFILE_FIELDS = ["role", "username", "email", "pharmacyId", "expiresAt"];

function nowSeconds() {
    return Math.floor(Date.now() / 1000);
}

/** Profil de session à partir d'une réponse de /api/auth (login, refresh, me), sans aucun autre champ. */
export function toSessionProfile(data) {
    return Object.fromEntries(PROFILE_FIELDS.map(field => [field, data?.[field] ?? null]));
}

export function isSessionExpired(auth, now = nowSeconds()) {
    const expiresAt = Number(auth?.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return true;
    return expiresAt <= now + EXPIRATION_LEEWAY_SECONDS;
}

export function shouldRefreshSession(auth, now = nowSeconds()) {
    const expiresAt = Number(auth?.expiresAt);
    if (!Number.isFinite(expiresAt)) return false;
    return expiresAt <= now + REFRESH_LEEWAY_SECONDS;
}

function readLastActivity() {
    const storedLastActivity = Number.parseInt(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || "", 10);
    return Number.isFinite(storedLastActivity) ? storedLastActivity : null;
}

export function readStoredAuth({ allowExpired = false } = {}) {
    try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!raw) return null;

        const auth = JSON.parse(raw);
        // Ancien format (JWT en clair) ou profil incomplet : retiré du poste, reconnexion nécessaire.
        if (!auth || auth.token || !Number.isFinite(auth.expiresAt)) {
            clearStoredSession({ notify: false });
            return null;
        }

        if (!allowExpired && isSessionExpired(auth)) {
            clearStoredSession({ expired: true, notify: false });
            return null;
        }

        const lastActivity = readLastActivity();
        if (lastActivity != null && Date.now() - lastActivity > getIdleTimeoutMs(auth.role)) {
            clearStoredSession({ expired: true, notify: false });
            return null;
        }

        return auth;
    } catch {
        return null;
    }
}

export function readSelectedPharmacy() {
    try {
        const raw = localStorage.getItem(SELECTED_PHARMACY_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function readPharmacyIdFromSession() {
    return readStoredAuth()?.pharmacyId ?? null;
}

export function clearStoredSession({ expired = false, notify = true } = {}) {
    try {
        if (expired) localStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, "true");
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(SELECTED_PHARMACY_STORAGE_KEY);
        localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    } catch {
        // localStorage can be unavailable in private browsing or tests.
    }
    purgeBusinessData();

    if (notify && typeof window !== "undefined") {
        window.dispatchEvent(new Event(AUTH_SESSION_CLEARED_EVENT));
    }
}

function storedIdentity() {
    try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!raw) return null;
        const stored = JSON.parse(raw);
        return stored?.email || null;
    } catch {
        return null;
    }
}

export function writeStoredAuth(auth) {
    const profile = toSessionProfile(auth);
    const previous = storedIdentity();
    if (previous && previous !== profile.email) purgeBusinessData();
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(profile));
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, Date.now().toString());
}

export function writeSelectedPharmacy(pharmacy) {
    if (pharmacy == null) localStorage.removeItem(SELECTED_PHARMACY_STORAGE_KEY);
    else localStorage.setItem(SELECTED_PHARMACY_STORAGE_KEY, JSON.stringify(pharmacy));
}
